import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils'
import {ProductListColConfig} from '@/services/MockData/Products/ProductListColConfig'
import {getProductCsvFile, getProductList} from '@/services/ProductsService/product'
import {DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {useQuery} from '@tanstack/react-query'
import {useMemo, useState} from 'react'
import {useDataTable} from './useDataTable-hook'
import {getInventoryFileUploadErrors} from '@/services/ProductsService/Fulfillment/inventory'

export const useProductList = () => {
  const {
    setSortingObj,
    setSearchArry,
    pageIndex,
    currentPage,
    sortingObj,
    searchArr,
    onPageIndexChange,
    createSearchArray,
    resetAllBtnClicked,
  } = useDataTable()

  const [btnDisabled, setBtnDisabled] = useState(false)

  const queryParams = useMemo(() => {
    const params = new URLSearchParams()

    // Use provided sort, or fallback to default
    if (sortingObj?.column) {
      params.append('sortBy', sortingObj.column)
      params.append('order', sortingObj.direction)
    } else {
      params.append('sortBy', 'productId')
      params.append('order', 'DESC')
    }

    // Always include pagination
    params.append('page', pageIndex)
    params.append('pageSize', DEFAULT_TABLE_PAGE_SIZE)

    return params.toString()
  }, [sortingObj?.column, sortingObj?.direction, pageIndex])

  const productResults = useQuery({
    queryKey: ['PRODUCT_LIST', queryParams, searchArr],
    queryFn: () => getProductList(searchArr, queryParams),
  })

  const onDownloadBtnClick = async selectedIds => {
    if (btnDisabled) {
      return
    }
    setBtnDisabled(true)
    const defaultSortingObj =
      Object.keys(queryParams)?.length > 0
        ? `&${queryParams}`
        : `sortBy=productId&order=DESC&page=${pageIndex}&pageSize=10`
    const body = {
      ids: selectedIds,
      filters: searchArr,
    }
    const data = await getProductCsvFile(body, defaultSortingObj)
    const fileName = `ProductList_${getDateWithTimeString()}`
    downloadCSVFiles(data, fileName)
    setBtnDisabled(false)
  }
  const productListData = useMemo(() => {
    const response = productResults?.data || {}

    if (!response?.data) return response

    const transformedData = response.data.map(item => {
      const subEn = item?.subCategories_en || ''

      const subEnCount =
        typeof subEn === 'string'
          ? subEn
              .split(',')
              .map(str => str.trim())
              .filter(Boolean).length
          : 0

      return {
        ...item,
        subCategories_en: subEnCount,
      }
    })

    return {
      ...response,
      data: transformedData,
    }
  }, [productResults?.data])

  const fetchUploadErrors = async parentId => {
    const res = await getInventoryFileUploadErrors(parentId)

    const productRows = res?.rows || res?.data || []

    const flattened = productRows.flatMap(row => {
      const pid = row?.productId ?? '-'
      const errorsArr = row?.errors || []

      // If no errors, return the default object
      if (!Array.isArray(errorsArr) || errorsArr.length === 0) {
        return [
          {
            productId: pid,
            errorCol: '',
          },
        ]
      }

      const combinedErrorMsg = errorsArr.map(errItem => `${errItem?.errorCol} - ${errItem?.errorMsg}`).join(' , ')

      return [
        {
          productId: pid,
          errorCol: combinedErrorMsg,
        },
      ]
    })
    return flattened
  }

  return {
    productListData,
    productListColConfigData: ProductListColConfig,
    currentPage,
    setSortingObj,
    setSearchArry,
    searchArr,
    onDownloadBtnClick,
    onPageIndexChange,
    isLoading: productResults?.isLoading || false,
    pageIndex,
    sortingObj,
    createSearchArray,
    resetAllBtnClicked,
    fetchUploadErrors,
  }
}
